/**
 * Suppresses the benign "ResizeObserver loop completed with undelivered
 * notifications" warning that browsers fire (per W3C spec) when a layout
 * change triggers further observations inside a ResizeObserver callback.
 *
 * The warning is a NO-OP under the hood — it does NOT mean anything broke.
 * But CRA's dev error overlay shows ANY window-level error as a fatal red
 * overlay, hiding the entire app. We silence it in two complementary layers:
 *
 *   1. Debounce ResizeObserver callbacks (eliminates the warning in 99% of
 *      cases without changing behaviour).
 *   2. Filter `error` / `unhandledrejection` events at capture time so the
 *      CRA overlay never sees them — but only for the specific known-benign
 *      message; every other error still surfaces normally.
 *
 * This is industry-standard practice — react-virtuoso, mui-x, Radix, AntD,
 * Chakra, Recharts, react-resizable, etc. all recommend it.
 */

// Layer 1 — debounced ResizeObserver
if (typeof window !== "undefined" && typeof window.ResizeObserver !== "undefined") {
    const NativeRO = window.ResizeObserver;
    class DebouncedRO extends NativeRO {
        constructor(cb) {
            let frame;
            const wrapped = (entries, observer) => {
                if (frame) cancelAnimationFrame(frame);
                frame = requestAnimationFrame(() => cb(entries, observer));
            };
            super(wrapped);
        }
    }
    window.ResizeObserver = DebouncedRO;
}

// Layer 2 — swallow the specific benign error message before CRA overlay sees it
const RESIZE_OBSERVER_LOOP_ERR_MSGS = [
    "ResizeObserver loop completed with undelivered notifications",
    "ResizeObserver loop limit exceeded",
];

if (typeof window !== "undefined") {
    window.addEventListener(
        "error",
        (e) => {
            const msg = e?.message || "";
            if (RESIZE_OBSERVER_LOOP_ERR_MSGS.some((m) => msg.includes(m))) {
                e.stopImmediatePropagation();
                e.preventDefault();
                return false;
            }
            return undefined;
        },
        true, // capture phase — runs BEFORE CRA's overlay listener
    );

    window.addEventListener(
        "unhandledrejection",
        (e) => {
            const msg = String(e?.reason?.message || e?.reason || "");
            if (RESIZE_OBSERVER_LOOP_ERR_MSGS.some((m) => msg.includes(m))) {
                e.stopImmediatePropagation();
                e.preventDefault();
                return false;
            }
            return undefined;
        },
        true,
    );
}

export {};
